import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      solutions: 'Solutions',
      process: 'Process',
      references: 'References',
      consultants: 'For Consultants',
      contact: 'Contact',
      getInTouch: 'Get in Touch',
    },
    // Hero Section
    hero: {
      eyebrow: 'Fountain Engineering Excellence',
      title1: 'Engineering',
      title2: 'Water, Light',
      title3: '& Motion',
      subtitle: 'From concept and 3D simulation to supply, installation assistance, and commissioning — we deliver complete fountain experiences for iconic projects worldwide.',
      cta1: 'Request a Consultation',
      cta2: 'Discuss Your Project',
    },
    // Why Magnum Section
    whyMagnum: {
      eyebrow: 'Why Magnum Fountain',
      title: 'More Than Equipment. Complete Project Partnership.',
      description: "We don't just supply fountain equipment. We engineer complete fountain experiences — from concept to commissioning, with European expertise for international projects.",
      points: [
        'European engineering standards',
        'End-to-end project support',
        'Advanced 3D simulation capabilities',
        'International project execution',
        'Premium equipment ecosystem',
        'Reliable execution & after-sales',
      ],
      yearsExperience: 'Years Experience',
    },
    // Services
    services: {
      eyebrow: 'Our Services',
      title: 'End-to-End Fountain Solutions',
      description: 'From initial concept to final commissioning, we provide comprehensive support at every stage of your fountain project.',
      viewAll: 'View All Services',
      items: [
        { title: 'Consulting & Design', description: 'Expert fountain consulting, feasibility studies, and innovative 3D concept development.' },
        { title: 'Engineering Support', description: 'Technical assistance for architects, engineers, and project consultants.' },
        { title: 'Show Visualization', description: 'Choreographed fountain simulations with advanced 3D visualization and DMX-512 control systems.' },
        { title: 'Equipment & Commissioning', description: 'Premium equipment supply, installation supervision, and startup assistance.' },
      ],
    },
    // Process
    process: {
      eyebrow: 'Our Process',
      title: 'From Concept to Commissioning',
      description: 'A structured approach ensuring excellence at every phase of your fountain project.',
      learnMore: 'Learn Our Full Process',
      steps: [
        { number: '01', title: 'Project Brief', desc: 'Understanding your vision' },
        { number: '02', title: 'Concept Design', desc: '3D simulation & visualization' },
        { number: '03', title: 'Technical Planning', desc: 'Engineering documentation' },
        { number: '04', title: 'Equipment Supply', desc: 'Premium components' },
        { number: '05', title: 'Commissioning', desc: 'Startup & optimization' },
      ],
    },
    // International Section
    international: {
      eyebrow: 'International Reach',
      title: 'European Expertise, Global Delivery',
      description: 'Based in Spain, we bring world-class fountain engineering to projects worldwide. We understand the unique challenges and opportunities of executing landmark projects across diverse markets and regions.',
      categories: [
        { title: 'Urban Projects', desc: 'Public plazas, municipal fountains, civic landmarks' },
        { title: 'Hospitality', desc: 'Hotels, resorts, entertainment venues' },
        { title: 'Landmark', desc: 'Iconic show fountains, cultural monuments' },
        { title: 'Private', desc: 'Real estate developers, private estates' },
      ],
    },
    // CTA Section
    cta: {
      eyebrow: 'Start Your Project',
      title: "Let's Engineer Your Vision",
      description: "Whether you're at the concept stage or ready to execute, our team is here to support your fountain project from start to finish.",
      button1: 'Request a Consultation',
      button2: 'WhatsApp Us',
    },
    // Stats
    stats: {
      years: 'Years Experience',
      projects: 'Projects Completed',
      countries: 'Countries Served',
      satisfaction: 'Client Satisfaction',
    },
    // Footer
    footer: {
      description: 'European fountain engineering expertise serving international projects. From concept to commissioning, we engineer complete fountain experiences.',
      services: 'Services',
      solutions: 'Solutions',
      company: 'Company',
      regionsServed: 'Regions Served',
      copyright: '© 2025 Magnum Fountain SL. All rights reserved.',
      tagline: 'European Engineering Excellence Worldwide',
    },
    // Contact Page
    contact: {
      eyebrow: 'Contact Us',
      title: "Let's Discuss Your Fountain Project",
      subtitle: "Whether you're at the concept stage or ready to execute, our team is here to support your fountain vision with European engineering expertise.",
      form: {
        title: 'Send Us a Message',
        name: 'Full Name',
        email: 'Email Address',
        company: 'Company / Organization',
        phone: 'Phone Number',
        projectType: 'Project Type',
        selectProjectType: 'Select project type',
        region: 'Project Region',
        selectRegion: 'Select region',
        message: 'Project Details',
        messagePlaceholder: 'Tell us about your project, timeline, and how we can help...',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Message Received!',
        successMessage: 'Thank you for reaching out. Our team will review your inquiry and get back to you within 24-48 hours.',
        sendAnother: 'Send Another Message',
      },
      directContact: 'Direct Contact',
      areasServed: 'Areas Served',
      ourOffice: 'Our Office',
      internationalClients: 'International Clients Welcome',
      internationalClientsDesc: 'We work with clients worldwide and can accommodate consultations in English, French, Spanish, and Arabic.',
      quickResponse: 'Quick Response',
      quickResponseDesc: 'We respond to all inquiries within 24-48 business hours.',
      noObligation: 'No Obligation',
      noObligationDesc: "Initial consultations are free. Let's explore how we can help.",
      globalReach: 'Global Reach',
      globalReachDesc: 'Based in Spain, we serve projects internationally.',
    },
    // About Page
    about: {
      eyebrow: 'About Magnum Fountain',
      title1: 'European Engineering.',
      title2: 'International Vision.',
      subtitle: 'Magnum Fountain SL is a Spain-based fountain engineering and consulting company dedicated to delivering world-class water feature projects internationally.',
      storyEyebrow: 'Our Story',
      storyTitle: 'More Than Suppliers — Your Project Partners',
      storyP1: 'Founded in 2013 with a clear mission: to bridge the gap between European fountain technology excellence and the growing demand for iconic water features worldwide.',
      storyP2: 'We recognized that successful fountain projects require more than quality equipment. They demand comprehensive technical support, from initial concept development through 3D visualization, engineering coordination, and hands-on commissioning assistance.',
      storyP3: 'Today, Magnum Fountain SL stands as a trusted partner for developers, governments, architects, and engineering firms seeking to create memorable fountain experiences that combine aesthetic impact with technical excellence.',
      startConversation: 'Start a Conversation',
      valuesEyebrow: 'Our Values',
      valuesTitle: 'What Drives Us',
      valuesDescription: 'The principles that guide every project we undertake and every relationship we build.',
      internationalEyebrow: 'International Reach',
      internationalTitle: 'Spain-Based, Internationally Focused',
      internationalDescription: "Our headquarters in Spain provides access to Europe's leading fountain technology ecosystem, while our international project experience ensures we understand local conditions, regulations, and execution challenges across diverse markets.",
      internationalPoints: [
        'Access to premium European equipment manufacturers',
        'Multi-language project support (English, French, Spanish, Arabic)',
        'Experience across diverse international regulatory environments',
        'Network of local partners for installation support',
      ],
      readyTitle: 'Ready to Work Together?',
      readyDescription: "Let's discuss how Magnum Fountain can support your next project with European engineering excellence.",
      contactUs: 'Contact Us Today',
    },
    // Services Page
    servicesPage: {
      eyebrow: 'Our Services',
      title: 'Comprehensive Fountain Engineering Services',
      subtitle: 'From initial concept to final commissioning, we provide end-to-end support for every phase of your fountain project.',
      whyChooseEyebrow: 'Why Choose Magnum',
      whyChooseTitle: 'Engineering Excellence at Every Step',
      whyChooseDescription: "We don't just supply equipment. We provide comprehensive technical support that ensures your fountain project is executed to the highest standards.",
      whyPoints: [
        { title: 'Single Point of Contact', desc: 'One partner for all your fountain project needs, from concept to commissioning.' },
        { title: 'European Quality Standards', desc: "Access to premium equipment and engineering practices from Europe's leading manufacturers." },
        { title: 'International Market Experience', desc: 'Deep understanding of executing complex projects across diverse international contexts.' },
      ],
      needSupport: 'Need Technical Support for Your Project?',
      needSupportDesc: "Whether you're an architect, engineer, or developer, we're here to help you navigate the complexities of fountain engineering.",
      discussRequirements: 'Discuss Your Requirements',
      forConsultants: 'For Consultants & Architects',
    },
    // Common
    common: {
      learnMore: 'Learn More',
      readMore: 'Read More',
      viewAll: 'View All',
      backToHome: 'Back to Home',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      about: 'À Propos',
      services: 'Services',
      solutions: 'Solutions',
      process: 'Processus',
      references: 'Références',
      consultants: 'Pour Consultants',
      contact: 'Contact',
      getInTouch: 'Nous Contacter',
    },
    // Hero Section
    hero: {
      eyebrow: "Excellence en Ingénierie de Fontaines",
      title1: 'Ingénierie',
      title2: "Eau, Lumière",
      title3: '& Mouvement',
      subtitle: "Du concept et simulation 3D à la fourniture, assistance à l'installation et mise en service — nous réalisons des expériences fontaines complètes pour des projets iconiques dans le monde entier.",
      cta1: 'Demander une Consultation',
      cta2: 'Discuter de Votre Projet',
    },
    // Why Magnum Section
    whyMagnum: {
      eyebrow: 'Pourquoi Magnum Fountain',
      title: "Plus qu'un Fournisseur. Un Partenaire de Projet Complet.",
      description: "Nous ne fournissons pas simplement des équipements de fontaines. Nous concevons des expériences fontaines complètes — du concept à la mise en service, avec une expertise européenne pour des projets internationaux.",
      points: [
        "Standards d'ingénierie européens",
        'Support projet de bout en bout',
        'Capacités avancées de simulation 3D',
        'Exécution de projets internationaux',
        "Écosystème d'équipements premium",
        'Exécution fiable & service après-vente',
      ],
      yearsExperience: "Années d'Expérience",
    },
    // Services
    services: {
      eyebrow: 'Nos Services',
      title: 'Solutions Fontaines de Bout en Bout',
      description: 'Du concept initial à la mise en service finale, nous fournissons un support complet à chaque étape de votre projet de fontaine.',
      viewAll: 'Voir Tous les Services',
      items: [
        { title: 'Conseil & Design', description: "Conseil expert en fontaines, études de faisabilité et développement de concepts 3D innovants." },
        { title: 'Support Ingénierie', description: 'Assistance technique pour architectes, ingénieurs et consultants de projet.' },
        { title: 'Visualisation de Spectacles', description: 'Simulations de fontaines chorégraphiées avec visualisation 3D avancée et systèmes de contrôle DMX-512.' },
        { title: 'Équipement & Mise en Service', description: "Fourniture d'équipements premium, supervision d'installation et assistance au démarrage." },
      ],
    },
    // Process
    process: {
      eyebrow: 'Notre Processus',
      title: 'Du Concept à la Mise en Service',
      description: "Une approche structurée garantissant l'excellence à chaque phase de votre projet de fontaine.",
      learnMore: 'Découvrir Notre Processus Complet',
      steps: [
        { number: '01', title: 'Brief Projet', desc: 'Comprendre votre vision' },
        { number: '02', title: 'Design Concept', desc: 'Simulation & visualisation 3D' },
        { number: '03', title: 'Planification Technique', desc: "Documentation d'ingénierie" },
        { number: '04', title: "Fourniture d'Équipements", desc: 'Composants premium' },
        { number: '05', title: 'Mise en Service', desc: 'Démarrage & optimisation' },
      ],
    },
    // International Section
    international: {
      eyebrow: 'Portée Internationale',
      title: 'Expertise Européenne, Livraison Mondiale',
      description: "Basés en Espagne, nous apportons une ingénierie de fontaines de classe mondiale aux projets du monde entier. Nous comprenons les défis et opportunités uniques de l'exécution de projets emblématiques sur divers marchés et régions.",
      categories: [
        { title: 'Projets Urbains', desc: 'Places publiques, fontaines municipales, monuments civiques' },
        { title: 'Hôtellerie', desc: 'Hôtels, resorts, lieux de divertissement' },
        { title: 'Monuments', desc: 'Fontaines spectaculaires iconiques, monuments culturels' },
        { title: 'Privé', desc: 'Promoteurs immobiliers, propriétés privées' },
      ],
    },
    // CTA Section
    cta: {
      eyebrow: 'Démarrer Votre Projet',
      title: 'Concrétisons Votre Vision',
      description: "Que vous soyez à l'étape du concept ou prêt à exécuter, notre équipe est là pour soutenir votre projet de fontaine du début à la fin.",
      button1: 'Demander une Consultation',
      button2: 'Nous Contacter sur WhatsApp',
    },
    // Stats
    stats: {
      years: "Années d'Expérience",
      projects: 'Projets Réalisés',
      countries: 'Pays Servis',
      satisfaction: 'Satisfaction Client',
    },
    // Footer
    footer: {
      description: "Expertise européenne en ingénierie de fontaines au service de projets internationaux. Du concept à la mise en service, nous concevons des expériences fontaines complètes.",
      services: 'Services',
      solutions: 'Solutions',
      company: 'Entreprise',
      regionsServed: 'Régions Desservies',
      copyright: '© 2025 Magnum Fountain SL. Tous droits réservés.',
      tagline: "Excellence d'Ingénierie Européenne dans le Monde",
    },
    // Contact Page
    contact: {
      eyebrow: 'Contactez-Nous',
      title: 'Discutons de Votre Projet de Fontaine',
      subtitle: "Que vous soyez à l'étape du concept ou prêt à exécuter, notre équipe est là pour soutenir votre vision avec une expertise d'ingénierie européenne.",
      form: {
        title: 'Envoyez-Nous un Message',
        name: 'Nom Complet',
        email: 'Adresse Email',
        company: 'Entreprise / Organisation',
        phone: 'Numéro de Téléphone',
        projectType: 'Type de Projet',
        selectProjectType: 'Sélectionner le type de projet',
        region: 'Région du Projet',
        selectRegion: 'Sélectionner la région',
        message: 'Détails du Projet',
        messagePlaceholder: 'Parlez-nous de votre projet, délais et comment nous pouvons vous aider...',
        submit: 'Envoyer le Message',
        sending: 'Envoi en cours...',
        success: 'Message Reçu !',
        successMessage: 'Merci de nous avoir contactés. Notre équipe examinera votre demande et vous répondra dans les 24-48 heures.',
        sendAnother: 'Envoyer un Autre Message',
      },
      directContact: 'Contact Direct',
      areasServed: 'Zones Desservies',
      ourOffice: 'Notre Bureau',
      internationalClients: 'Clients Internationaux Bienvenus',
      internationalClientsDesc: 'Nous travaillons avec des clients du monde entier et pouvons effectuer des consultations en anglais, français, espagnol et arabe.',
      quickResponse: 'Réponse Rapide',
      quickResponseDesc: 'Nous répondons à toutes les demandes sous 24-48 heures ouvrables.',
      noObligation: 'Sans Engagement',
      noObligationDesc: 'Les consultations initiales sont gratuites. Explorons comment nous pouvons vous aider.',
      globalReach: 'Portée Mondiale',
      globalReachDesc: 'Basés en Espagne, nous servons des projets internationalement.',
    },
    // About Page
    about: {
      eyebrow: 'À Propos de Magnum Fountain',
      title1: 'Ingénierie Européenne.',
      title2: 'Vision Internationale.',
      subtitle: "Magnum Fountain SL est une entreprise d'ingénierie et de conseil en fontaines basée en Espagne, dédiée à la réalisation de projets de jeux d'eau de classe mondiale à l'international.",
      storyEyebrow: 'Notre Histoire',
      storyTitle: "Plus que Fournisseurs — Vos Partenaires de Projet",
      storyP1: "Fondée en 2013 avec une mission claire : combler le fossé entre l'excellence technologique européenne en fontaines et la demande croissante de jeux d'eau iconiques dans le monde entier.",
      storyP2: "Nous avons reconnu que les projets de fontaines réussis nécessitent plus que des équipements de qualité. Ils demandent un support technique complet, du développement initial du concept à la visualisation 3D, la coordination d'ingénierie et l'assistance pratique à la mise en service.",
      storyP3: "Aujourd'hui, Magnum Fountain SL est un partenaire de confiance pour les promoteurs, gouvernements, architectes et bureaux d'études cherchant à créer des expériences fontaines mémorables alliant impact esthétique et excellence technique.",
      startConversation: 'Démarrer une Conversation',
      valuesEyebrow: 'Nos Valeurs',
      valuesTitle: 'Ce Qui Nous Anime',
      valuesDescription: 'Les principes qui guident chaque projet que nous entreprenons et chaque relation que nous construisons.',
      internationalEyebrow: 'Portée Internationale',
      internationalTitle: 'Basés en Espagne, Focus International',
      internationalDescription: "Notre siège en Espagne donne accès à l'écosystème technologique de fontaines leader en Europe, tandis que notre expérience de projets internationaux nous permet de comprendre les conditions locales, réglementations et défis d'exécution sur divers marchés.",
      internationalPoints: [
        "Accès aux fabricants d'équipements européens premium",
        'Support projet multilingue (anglais, français, espagnol, arabe)',
        'Expérience dans divers environnements réglementaires internationaux',
        "Réseau de partenaires locaux pour le support d'installation",
      ],
      readyTitle: 'Prêt à Travailler Ensemble ?',
      readyDescription: "Discutons de la façon dont Magnum Fountain peut soutenir votre prochain projet avec l'excellence d'ingénierie européenne.",
      contactUs: 'Contactez-Nous Aujourd\'hui',
    },
    // Services Page
    servicesPage: {
      eyebrow: 'Nos Services',
      title: "Services Complets d'Ingénierie de Fontaines",
      subtitle: "Du concept initial à la mise en service finale, nous fournissons un support de bout en bout pour chaque phase de votre projet de fontaine.",
      whyChooseEyebrow: 'Pourquoi Choisir Magnum',
      whyChooseTitle: "Excellence d'Ingénierie à Chaque Étape",
      whyChooseDescription: "Nous ne fournissons pas simplement des équipements. Nous fournissons un support technique complet qui garantit que votre projet de fontaine est exécuté selon les plus hauts standards.",
      whyPoints: [
        { title: 'Point de Contact Unique', desc: 'Un partenaire pour tous vos besoins de projet de fontaine, du concept à la mise en service.' },
        { title: 'Standards de Qualité Européens', desc: "Accès aux équipements premium et pratiques d'ingénierie des principaux fabricants européens." },
        { title: 'Expérience du Marché International', desc: "Compréhension approfondie de l'exécution de projets complexes dans divers contextes internationaux." },
      ],
      needSupport: 'Besoin de Support Technique pour Votre Projet ?',
      needSupportDesc: "Que vous soyez architecte, ingénieur ou promoteur, nous sommes là pour vous aider à naviguer dans les complexités de l'ingénierie des fontaines.",
      discussRequirements: 'Discuter de Vos Besoins',
      forConsultants: 'Pour Consultants & Architectes',
    },
    // Common
    common: {
      learnMore: 'En Savoir Plus',
      readMore: 'Lire Plus',
      viewAll: 'Voir Tout',
      backToHome: "Retour à l'Accueil",
    },
  },
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      about: 'Nosotros',
      services: 'Servicios',
      solutions: 'Soluciones',
      process: 'Proceso',
      references: 'Referencias',
      consultants: 'Para Consultores',
      contact: 'Contacto',
      getInTouch: 'Contáctenos',
    },
    // Hero Section
    hero: {
      eyebrow: 'Excelencia en Ingeniería de Fuentes',
      title1: 'Ingeniería de',
      title2: 'Agua, Luz',
      title3: '& Movimiento',
      subtitle: 'Desde el concepto y simulación 3D hasta el suministro, asistencia en instalación y puesta en marcha — entregamos experiencias completas de fuentes para proyectos icónicos en todo el mundo.',
      cta1: 'Solicitar Consulta',
      cta2: 'Discutir Su Proyecto',
    },
    // Why Magnum Section
    whyMagnum: {
      eyebrow: 'Por Qué Magnum Fountain',
      title: 'Más Que Equipos. Socio Completo de Proyecto.',
      description: 'No solo suministramos equipos de fuentes. Diseñamos experiencias completas de fuentes — desde el concepto hasta la puesta en marcha, con experiencia europea para proyectos internacionales.',
      points: [
        'Estándares de ingeniería europeos',
        'Soporte de proyecto de extremo a extremo',
        'Capacidades avanzadas de simulación 3D',
        'Ejecución de proyectos internacionales',
        'Ecosistema de equipos premium',
        'Ejecución confiable y postventa',
      ],
      yearsExperience: 'Años de Experiencia',
    },
    // Services
    services: {
      eyebrow: 'Nuestros Servicios',
      title: 'Soluciones de Fuentes de Extremo a Extremo',
      description: 'Desde el concepto inicial hasta la puesta en marcha final, proporcionamos soporte integral en cada etapa de su proyecto de fuente.',
      viewAll: 'Ver Todos los Servicios',
      items: [
        { title: 'Consultoría & Diseño', description: 'Consultoría experta en fuentes, estudios de viabilidad y desarrollo de conceptos 3D innovadores.' },
        { title: 'Soporte de Ingeniería', description: 'Asistencia técnica para arquitectos, ingenieros y consultores de proyecto.' },
        { title: 'Visualización de Shows', description: 'Simulaciones de fuentes coreografiadas con visualización 3D avanzada y sistemas de control DMX-512.' },
        { title: 'Equipos & Puesta en Marcha', description: 'Suministro de equipos premium, supervisión de instalación y asistencia en arranque.' },
      ],
    },
    // Process
    process: {
      eyebrow: 'Nuestro Proceso',
      title: 'Del Concepto a la Puesta en Marcha',
      description: 'Un enfoque estructurado que garantiza la excelencia en cada fase de su proyecto de fuente.',
      learnMore: 'Conocer Nuestro Proceso Completo',
      steps: [
        { number: '01', title: 'Brief del Proyecto', desc: 'Entendiendo su visión' },
        { number: '02', title: 'Diseño Conceptual', desc: 'Simulación 3D y visualización' },
        { number: '03', title: 'Planificación Técnica', desc: 'Documentación de ingeniería' },
        { number: '04', title: 'Suministro de Equipos', desc: 'Componentes premium' },
        { number: '05', title: 'Puesta en Marcha', desc: 'Arranque y optimización' },
      ],
    },
    // International Section
    international: {
      eyebrow: 'Alcance Internacional',
      title: 'Experiencia Europea, Entrega Global',
      description: 'Con sede en España, llevamos ingeniería de fuentes de clase mundial a proyectos en todo el mundo. Entendemos los desafíos y oportunidades únicos de ejecutar proyectos emblemáticos en diversos mercados y regiones.',
      categories: [
        { title: 'Proyectos Urbanos', desc: 'Plazas públicas, fuentes municipales, monumentos cívicos' },
        { title: 'Hostelería', desc: 'Hoteles, resorts, lugares de entretenimiento' },
        { title: 'Monumentos', desc: 'Fuentes espectaculares icónicas, monumentos culturales' },
        { title: 'Privado', desc: 'Promotores inmobiliarios, propiedades privadas' },
      ],
    },
    // CTA Section
    cta: {
      eyebrow: 'Iniciar Su Proyecto',
      title: 'Hagamos Realidad Su Visión',
      description: 'Ya sea que esté en la etapa de concepto o listo para ejecutar, nuestro equipo está aquí para apoyar su proyecto de fuente de principio a fin.',
      button1: 'Solicitar Consulta',
      button2: 'WhatsApp',
    },
    // Stats
    stats: {
      years: 'Años de Experiencia',
      projects: 'Proyectos Realizados',
      countries: 'Países Atendidos',
      satisfaction: 'Satisfacción del Cliente',
    },
    // Footer
    footer: {
      description: 'Experiencia europea en ingeniería de fuentes sirviendo proyectos internacionales. Desde el concepto hasta la puesta en marcha, diseñamos experiencias completas de fuentes.',
      services: 'Servicios',
      solutions: 'Soluciones',
      company: 'Empresa',
      regionsServed: 'Regiones Atendidas',
      copyright: '© 2025 Magnum Fountain SL. Todos los derechos reservados.',
      tagline: 'Excelencia de Ingeniería Europea en el Mundo',
    },
    // Contact Page
    contact: {
      eyebrow: 'Contáctenos',
      title: 'Hablemos de Su Proyecto de Fuente',
      subtitle: 'Ya sea que esté en la etapa de concepto o listo para ejecutar, nuestro equipo está aquí para apoyar su visión con experiencia de ingeniería europea.',
      form: {
        title: 'Envíenos un Mensaje',
        name: 'Nombre Completo',
        email: 'Correo Electrónico',
        company: 'Empresa / Organización',
        phone: 'Número de Teléfono',
        projectType: 'Tipo de Proyecto',
        selectProjectType: 'Seleccionar tipo de proyecto',
        region: 'Región del Proyecto',
        selectRegion: 'Seleccionar región',
        message: 'Detalles del Proyecto',
        messagePlaceholder: 'Cuéntenos sobre su proyecto, plazos y cómo podemos ayudar...',
        submit: 'Enviar Mensaje',
        sending: 'Enviando...',
        success: '¡Mensaje Recibido!',
        successMessage: 'Gracias por contactarnos. Nuestro equipo revisará su consulta y le responderá en 24-48 horas.',
        sendAnother: 'Enviar Otro Mensaje',
      },
      directContact: 'Contacto Directo',
      areasServed: 'Áreas Atendidas',
      ourOffice: 'Nuestra Oficina',
      internationalClients: 'Clientes Internacionales Bienvenidos',
      internationalClientsDesc: 'Trabajamos con clientes de todo el mundo y podemos realizar consultas en inglés, francés, español y árabe.',
      quickResponse: 'Respuesta Rápida',
      quickResponseDesc: 'Respondemos a todas las consultas en 24-48 horas hábiles.',
      noObligation: 'Sin Compromiso',
      noObligationDesc: 'Las consultas iniciales son gratuitas. Exploremos cómo podemos ayudar.',
      globalReach: 'Alcance Global',
      globalReachDesc: 'Con sede en España, servimos proyectos internacionalmente.',
    },
    // About Page
    about: {
      eyebrow: 'Sobre Magnum Fountain',
      title1: 'Ingeniería Europea.',
      title2: 'Visión Internacional.',
      subtitle: 'Magnum Fountain SL es una empresa de ingeniería y consultoría de fuentes con sede en España, dedicada a entregar proyectos de fuentes de clase mundial internacionalmente.',
      storyEyebrow: 'Nuestra Historia',
      storyTitle: 'Más Que Proveedores — Sus Socios de Proyecto',
      storyP1: 'Fundada en 2013 con una misión clara: cerrar la brecha entre la excelencia tecnológica europea en fuentes y la creciente demanda de fuentes icónicas en todo el mundo.',
      storyP2: 'Reconocimos que los proyectos de fuentes exitosos requieren más que equipos de calidad. Demandan soporte técnico integral, desde el desarrollo inicial del concepto hasta la visualización 3D, coordinación de ingeniería y asistencia práctica en la puesta en marcha.',
      storyP3: 'Hoy, Magnum Fountain SL es un socio de confianza para promotores, gobiernos, arquitectos y oficinas de ingeniería que buscan crear experiencias de fuentes memorables que combinen impacto estético con excelencia técnica.',
      startConversation: 'Iniciar una Conversación',
      valuesEyebrow: 'Nuestros Valores',
      valuesTitle: 'Lo Que Nos Impulsa',
      valuesDescription: 'Los principios que guían cada proyecto que emprendemos y cada relación que construimos.',
      internationalEyebrow: 'Alcance Internacional',
      internationalTitle: 'Con Sede en España, Enfoque Internacional',
      internationalDescription: 'Nuestra sede en España proporciona acceso al ecosistema tecnológico de fuentes líder en Europa, mientras que nuestra experiencia en proyectos internacionales nos permite entender las condiciones locales, regulaciones y desafíos de ejecución en diversos mercados.',
      internationalPoints: [
        'Acceso a fabricantes de equipos europeos premium',
        'Soporte de proyecto multilingüe (inglés, francés, español, árabe)',
        'Experiencia en diversos entornos regulatorios internacionales',
        'Red de socios locales para soporte de instalación',
      ],
      readyTitle: '¿Listo para Trabajar Juntos?',
      readyDescription: 'Hablemos de cómo Magnum Fountain puede apoyar su próximo proyecto con excelencia de ingeniería europea.',
      contactUs: 'Contáctenos Hoy',
    },
    // Services Page
    servicesPage: {
      eyebrow: 'Nuestros Servicios',
      title: 'Servicios Integrales de Ingeniería de Fuentes',
      subtitle: 'Desde el concepto inicial hasta la puesta en marcha final, proporcionamos soporte de extremo a extremo para cada fase de su proyecto de fuente.',
      whyChooseEyebrow: 'Por Qué Elegir Magnum',
      whyChooseTitle: 'Excelencia de Ingeniería en Cada Paso',
      whyChooseDescription: 'No solo suministramos equipos. Proporcionamos soporte técnico integral que garantiza que su proyecto de fuente se ejecute con los más altos estándares.',
      whyPoints: [
        { title: 'Punto de Contacto Único', desc: 'Un socio para todas sus necesidades de proyecto de fuente, desde el concepto hasta la puesta en marcha.' },
        { title: 'Estándares de Calidad Europeos', desc: 'Acceso a equipos premium y prácticas de ingeniería de los principales fabricantes europeos.' },
        { title: 'Experiencia en Mercado Internacional', desc: 'Comprensión profunda de la ejecución de proyectos complejos en diversos contextos internacionales.' },
      ],
      needSupport: '¿Necesita Soporte Técnico para Su Proyecto?',
      needSupportDesc: 'Ya sea arquitecto, ingeniero o promotor, estamos aquí para ayudarle a navegar las complejidades de la ingeniería de fuentes.',
      discussRequirements: 'Discutir Sus Requisitos',
      forConsultants: 'Para Consultores y Arquitectos',
    },
    // Common
    common: {
      learnMore: 'Saber Más',
      readMore: 'Leer Más',
      viewAll: 'Ver Todo',
      backToHome: 'Volver al Inicio',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('magnum-language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('magnum-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
